import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  User,
  UserCheck,
  CreditCard,
  Calendar,
  Package,
  MessageSquare,
  Send,
  Paperclip,
  X,
  Building2
} from 'lucide-react';
import { getSaleById, addSaleComment } from '../../api/sales';
import { toast } from 'react-toastify';
import ReceiptActions from '../../components/receipts/ReceiptActions';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const SaleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Validate ID is numeric on mount
  useEffect(() => {
    const numericId = parseInt(id);
    if (isNaN(numericId) || numericId <= 0) {
      toast.error('Invalid sale ID');
      navigate('/sales');
    }
  }, [id, navigate]);

  const fetchSaleDetails = useCallback(async () => {
    // Double check ID is numeric before fetching
    const numericId = parseInt(id);
    if (isNaN(numericId) || numericId <= 0) {
      return;
    }

    setLoading(true);
    try {
      const response = await getSaleById(id);
      setSale(response.sale);
    } catch (error) {
      toast.error('Failed to load sale details');
      navigate('/sales');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchSaleDetails();
  }, [fetchSaleDetails]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setCommentFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setCommentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!commentText.trim() && commentFiles.length === 0) {
      toast.error('Please enter a comment or attach files');
      return;
    }

    setSubmittingComment(true);

    try {
      const formData = new FormData();
      formData.append('comment_text', commentText.trim());

      commentFiles.forEach((file) => {
        formData.append('attachments', file);
      });

      await addSaleComment(id, formData);

      toast.success('Comment added successfully');
      setCommentText('');
      setCommentFiles([]);
      fetchSaleDetails(); // Refresh to get new comment
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      paid: 'text-green-600 bg-green-100',
      partial: 'text-yellow-600 bg-yellow-100',
      unpaid: 'text-red-600 bg-red-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  if (loading) {
    return <LoadingSpinner message="Loading sale details..." className="min-h-screen" />;
  }

  if (!sale) {
    return null;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <button
              type="button"
              onClick={() => navigate('/sales')}
              className="flex items-center gap-2 text-secondary-gray hover:text-primary-black transition-colors self-start"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="whitespace-nowrap text-sm sm:text-base">Back to Sales</span>
            </button>
            <ReceiptActions sale={sale} />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-primary-black">Sale Details</h1>
              <p className="text-base sm:text-lg text-accent-gold font-semibold">
                {sale.receipt_number}
              </p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs sm:text-sm text-secondary-gray">Sale Date</p>
              <p className="text-base sm:text-lg font-semibold text-primary-black">
                {new Date(sale.sale_date).toLocaleDateString()}
              </p>
              <p className="text-xs sm:text-sm text-secondary-gray">
                {new Date(sale.sale_date).toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Sale Items & Details */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Sale Items */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="bg-primary-black text-white px-4 sm:px-6 py-3 flex items-center gap-2">
                <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                <h2 className="text-sm sm:text-base font-semibold">Sale Items</h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[500px]">
                  <thead className="bg-secondary-offWhite">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-primary-black">
                        Product
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-primary-black">
                        Price
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-primary-black">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-primary-black">
                        Discount
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-primary-black">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-lightGray">
                    {sale.items &&
                      sale.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-primary-black">{item.product_name}</p>
                              <span
                                className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${item.product_type === 'accessory' ? 'bg-purple-100 text-purple-700 border border-purple-300' : 'bg-blue-100 text-blue-700 border border-blue-300'}`}
                              >
                                {item.product_type === 'accessory' ? 'ACC' : 'TILE'}
                              </span>
                            </div>
                            <p className="text-xs text-secondary-gray">{item.product_code}</p>
                          </td>
                          <td className="px-4 py-3 text-right">
                            ₦{parseFloat(item.selling_price).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">{item.quantity}</td>
                          <td className="px-4 py-3 text-right text-red-600">
                            {item.unit_discount > 0
                              ? `-₦${parseFloat(item.unit_discount).toLocaleString()}`
                              : '-'}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-accent-gold">
                            ₦{parseFloat(item.line_subtotal).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="border-t-2 border-secondary-lightGray bg-secondary-offWhite p-4 sm:p-6">
                <div className="space-y-2 max-w-md sm:ml-auto">
                  <div className="flex justify-between text-sm">
                    <span className="text-secondary-gray">Subtotal:</span>
                    <span className="font-medium">
                      ₦{parseFloat(sale.subtotal).toLocaleString()}
                    </span>
                  </div>
                  {sale.total_discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-secondary-gray">Discount:</span>
                      <span className="font-medium text-red-600">
                        -₦{parseFloat(sale.total_discount).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-secondary-lightGray">
                    <span className="text-primary-black">Total Amount:</span>
                    <span className="text-accent-gold">
                      ₦{parseFloat(sale.total_amount).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-secondary-lightGray">
                    <span className="text-secondary-gray">Amount Paid:</span>
                    <span className="font-medium text-green-600">
                      ₦{parseFloat(sale.amount_paid).toLocaleString()}
                    </span>
                  </div>
                  {sale.amount_due > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-secondary-gray">Amount Due:</span>
                      <span className="font-bold text-red-600">
                        ₦{parseFloat(sale.amount_due).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Comments Section */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="bg-primary-black text-white px-4 sm:px-6 py-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                <h2 className="text-sm sm:text-base font-semibold">Comments & Notes</h2>
              </div>

              <div className="p-4 sm:p-6">
                {/* Add Comment Form */}
                <form onSubmit={handleSubmitComment} className="mb-4 sm:mb-6">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none mb-2 text-sm sm:text-base"
                    placeholder="Add a comment or note about this sale..."
                  />

                  {/* File Attachments */}
                  {commentFiles.length > 0 && (
                    <div className="mb-3 space-y-2">
                      {commentFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-secondary-offWhite rounded"
                        >
                          <span className="text-sm text-secondary-gray truncate flex-1">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                            title="Remove file"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <label className="flex items-center gap-2 px-4 py-2 bg-white border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors cursor-pointer">
                      <Paperclip className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm whitespace-nowrap">Attach Files</span>
                      <input type="file" multiple onChange={handleFileChange} className="hidden" />
                    </label>
                    <button
                      type="submit"
                      disabled={submittingComment}
                      className="flex items-center gap-2 px-6 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-5 h-5 flex-shrink-0" />
                      <span className="whitespace-nowrap">
                        {submittingComment ? 'Posting...' : 'Post Comment'}
                      </span>
                    </button>
                  </div>
                </form>

                {/* Existing Comments */}
                <div className="space-y-4">
                  {sale.comments && sale.comments.length > 0 ? (
                    sale.comments.map((comment) => (
                      <div
                        key={comment.id}
                        className="border border-secondary-lightGray rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-semibold text-primary-black">{comment.user_name}</p>
                          <p className="text-xs text-secondary-gray">
                            {new Date(comment.created_at).toLocaleString()}
                          </p>
                        </div>
                        <p className="text-secondary-gray mb-2">{comment.comment_text}</p>
                        {comment.attachments && comment.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {comment.attachments.map((file, idx) => (
                              <a
                                key={idx}
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs px-2 py-1 bg-accent-gold text-white rounded hover:bg-yellow-600"
                              >
                                {file.filename}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-secondary-gray py-4">No comments yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Customer & Payment Info */}
          <div className="space-y-4 sm:space-y-6">
            {/* Customer Info */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-accent-gold" />
                <h2 className="text-sm sm:text-base font-semibold text-primary-black">
                  Customer Information
                </h2>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-secondary-gray">Name</p>
                  <p className="font-medium text-primary-black">{sale.customer_name}</p>
                </div>
                <div>
                  <p className="text-secondary-gray">Phone</p>
                  <p className="font-medium text-primary-black">{sale.customer_phone}</p>
                </div>
                {sale.customer_email && (
                  <div>
                    <p className="text-secondary-gray">Email</p>
                    <p className="font-medium text-primary-black">{sale.customer_email}</p>
                  </div>
                )}
                {sale.customer_address && (
                  <div>
                    <p className="text-secondary-gray">Address</p>
                    <p className="font-medium text-primary-black">{sale.customer_address}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Branch Info */}
            {sale.branch_name && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-accent-gold" />
                  <h2 className="text-sm sm:text-base font-semibold text-primary-black">
                    Branch Information
                  </h2>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-secondary-gray">Branch Name</p>
                    <p className="font-medium text-primary-black">{sale.branch_name}</p>
                  </div>
                  {sale.branch_code && (
                    <div>
                      <p className="text-secondary-gray">Branch Code</p>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {sale.branch_code}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Referrer Info */}
            {sale.referrer_name && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-accent-gold" />
                  <h2 className="text-sm sm:text-base font-semibold text-primary-black">
                    Referrer Information
                  </h2>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-secondary-gray">Name</p>
                    <p className="font-medium text-primary-black">{sale.referrer_name}</p>
                  </div>
                  <div>
                    <p className="text-secondary-gray">Commission Rate</p>
                    <p className="font-medium text-accent-gold">{sale.commission_rate}%</p>
                  </div>
                  {sale.commission_amount > 0 && (
                    <div>
                      <p className="text-secondary-gray">Commission Earned</p>
                      <p className="font-bold text-green-600">
                        ₦{parseFloat(sale.commission_amount).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Info */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-accent-gold" />
                <h2 className="text-sm sm:text-base font-semibold text-primary-black">
                  Payment Information
                </h2>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-secondary-gray mb-1">Payment Method</p>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    {sale.payment_method.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-secondary-gray mb-1">Payment Status</p>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(sale.payment_status)}`}
                  >
                    {sale.payment_status.charAt(0).toUpperCase() + sale.payment_status.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Profit Info */}
            {sale.gross_profit !== undefined && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-secondary-lightGray">
                <h2 className="text-sm sm:text-base font-semibold text-primary-black mb-3 sm:mb-4">
                  Profit Analysis
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-secondary-gray">Total Cost:</span>
                    <span className="text-secondary-gray">
                      ₦{parseFloat(sale.total_cost).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-gray">
                      {parseFloat(sale.gross_profit) >= 0 ? 'Gross Profit:' : 'Gross Loss:'}
                    </span>
                    <span
                      className={`font-medium ${parseFloat(sale.gross_profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      ₦{parseFloat(sale.gross_profit).toLocaleString()}
                    </span>
                  </div>
                  {sale.referrer_commission_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-secondary-gray">Referrer Commission:</span>
                      <span className="text-yellow-600">
                        -₦{parseFloat(sale.referrer_commission_amount).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-secondary-lightGray">
                    <span className="font-semibold text-primary-black">
                      {parseFloat(sale.net_profit || sale.gross_profit) >= 0
                        ? 'Net Profit:'
                        : 'Net Loss:'}
                    </span>
                    <span
                      className={`font-bold ${parseFloat(sale.net_profit || sale.gross_profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      ₦{parseFloat(sale.net_profit || sale.gross_profit).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            {sale.notes && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="font-semibold text-primary-black mb-2">Notes</h2>
                <p className="text-sm text-secondary-gray">{sale.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaleDetail;
